import type {ReactElement} from 'react';
import React from 'react';
import styles from './submit-button.module.scss';
import type {AsyncVoidFunction} from '@Utils/general.util';
import type {AuthenticateModalContent} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {ClickableDiv} from '@Components/clickable-div';

interface SubmitButtonProps {
  isLoading: boolean;
  isActive: boolean;
  onSubmission: AsyncVoidFunction;
  showUpsellDialog: boolean;
  upsellDialog?: string;
  authenticateModalContent?: AuthenticateModalContent;
  isEditableDuringGeneration?: boolean;
}

export function SubmitButton({isLoading, isActive, onSubmission, ...props}: SubmitButtonProps): ReactElement {
  const getRegenerateButton = (): ReactElement => {
    return (
      <ClickableDiv className={`radius-round flex-center bg-ai-dark content-body-white _cursor-pointer ${styles.submitButtonContainer}`} onClick={handleClick}>
        <Icon icon={'icon-rotate-ccw'} size={IconSize.SIZE_ICON_16} type={IconType.SECONDARY} />
      </ClickableDiv>
    );
  };

  const getLoader = (): ReactElement => {
    return (
      <div className={`spacing-p-r-2 spacing-p-l-2 ${styles.loader} `}>
        <CircularProgressLoader color={LOADER_COLOR.AI_DARK} size={LOADER_SIZE.XSMALL} />
      </div>
    );
  };

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (props.authenticateModalContent?.showModal) {
      openAuthenticateModal(props.authenticateModalContent.modalProps);
      return;
    }

    if (props.showUpsellDialog) {
      void window.PMW.showPremiumOnlyFeatureDialog(props.upsellDialog ?? '');
      return;
    }

    void onSubmission();
  };

  const getButton = (): ReactElement => {
    return (
      <ClickableDiv className={`radius-round flex-center ${styles.submitButtonContainer} ${!isActive ? styles.submitButtonDisabled : 'bg-ai-dark'}`} onClick={handleClick}>
        <Icon icon={'icon-chat-send'} size={IconSize.SIZE_ICON_16} type={IconType.SECONDARY} isDisabled={!isActive} />
      </ClickableDiv>
    );
  };

  const getSubmitButton = (): ReactElement => {
    if (isLoading && props.isEditableDuringGeneration) {
      return getRegenerateButton();
    }

    if (isLoading) {
      return getLoader();
    }

    return getButton();
  };

  return getSubmitButton();
}
